import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Icona } from './Icona';

export type ValorDesplegable = string | number;

export interface OpcioDesplegable<V extends ValorDesplegable = ValorDesplegable> {
  valor: V;
  etiqueta: string;
  /** Text secundari a la dreta o sota (codi, àrea, comptador…). */
  detall?: string;
  /** Capçalera de grup: les opcions consecutives amb el mateix grup s'agrupen. */
  grup?: string;
  /** Sagnat (p. ex. jerarquia departament › àrea › servei). */
  nivell?: number;
  desactivada?: boolean;
  icona?: ReactNode;
}

export interface DesplegableProps<V extends ValorDesplegable> {
  valor: V | '' | null | undefined;
  opcions: OpcioDesplegable<V>[];
  onCanvia: (valor: V) => void;
  /** Text quan no hi ha cap opció triada. */
  placeholder?: string;
  'aria-label'?: string;
  id?: string;
  disabled?: boolean;
  /** `input`: camp de formulari; `chip`: filtre arrodonit (barres de filtres); `compacte`: dins de taules. */
  variant?: 'input' | 'chip' | 'compacte';
  /** Només per a `chip`: es pinta ressaltat (filtre aplicat). */
  actiu?: boolean;
  /** Mostra un cercador dins la llista. Per defecte, si hi ha més de 8 opcions. */
  cerca?: boolean;
  className?: string;
  /** Text si la cerca no troba res. */
  senseResultats?: string;
}

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Desplegable propi del CAI (substitueix `<select>`): llista arrodonida amb ombra, opcions
 * agrupades o sagnades, cercador quan n'hi ha moltes i navegació completa amb teclat.
 * La llista es pinta en un portal amb posició fixa perquè no la retallin panells amb `overflow`.
 */
export function Desplegable<V extends ValorDesplegable>({
  valor,
  opcions,
  onCanvia,
  placeholder,
  'aria-label': ariaLabel,
  id,
  disabled,
  variant = 'input',
  actiu,
  cerca,
  className,
  senseResultats,
}: DesplegableProps<V>) {
  const { t } = useTranslation('comu');
  const idBase = useId();
  const idLlista = `${idBase}-llista`;
  const boto = useRef<HTMLButtonElement>(null);
  const llista = useRef<HTMLDivElement>(null);
  const camp = useRef<HTMLInputElement>(null);
  const [obert, setObert] = useState(false);
  const [text, setText] = useState('');
  const [actiuIdx, setActiuIdx] = useState(-1);
  const [pos, setPos] = useState<CSSProperties>({});
  const ambCerca = cerca ?? opcions.length > 8;

  const seleccionada = opcions.find((o) => o.valor === valor && valor !== '' && valor != null);
  // Una opció amb valor '' fa de «Tots/Cap»: es mostra com a seleccionada si el valor és buit.
  const buida = opcions.find((o) => o.valor === '');
  const mostrada = seleccionada ?? (valor === '' || valor == null ? buida : undefined);

  const filtrades = useMemo(() => {
    const q = normalitza(text.trim());
    if (!q) return opcions;
    return opcions.filter((o) => normalitza(`${o.etiqueta} ${o.detall ?? ''}`).includes(q));
  }, [opcions, text]);

  const habilitades = filtrades.filter((o) => !o.desactivada);

  const recolloca = useCallback(() => {
    const el = boto.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const alturaMax = 320;
    const espaiSota = window.innerHeight - r.bottom - 12;
    const amunt = espaiSota < 200 && r.top > espaiSota;
    const amplada = Math.max(r.width, variant === 'chip' ? 220 : 180);
    const left = Math.min(r.left, window.innerWidth - amplada - 8);
    setPos({
      position: 'fixed',
      left: Math.max(8, left),
      minWidth: amplada,
      maxWidth: Math.max(amplada, 360),
      ...(amunt
        ? { bottom: window.innerHeight - r.top + 6, maxHeight: Math.min(alturaMax, r.top - 12) }
        : { top: r.bottom + 6, maxHeight: Math.min(alturaMax, Math.max(espaiSota, 160)) }),
    });
  }, [variant]);

  const obre = () => {
    if (disabled) return;
    setText('');
    const idx = filtrades.findIndex((o) => o === mostrada);
    setActiuIdx(idx >= 0 ? idx : opcions.findIndex((o) => !o.desactivada));
    recolloca();
    setObert(true);
  };
  const tanca = (retornaFocus = true) => {
    setObert(false);
    if (retornaFocus) boto.current?.focus();
  };
  const tria = (o: OpcioDesplegable<V>) => {
    if (o.desactivada) return;
    if (o.valor !== valor) onCanvia(o.valor);
    tanca();
  };

  useLayoutEffect(() => {
    if (!obert) return;
    if (ambCerca) camp.current?.focus();
    else llista.current?.focus();
  }, [obert, ambCerca]);

  useEffect(() => {
    if (!obert) return;
    const fora = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!boto.current?.contains(n) && !llista.current?.contains(n)) tanca(false);
    };
    const mou = () => recolloca();
    document.addEventListener('mousedown', fora);
    window.addEventListener('resize', mou);
    window.addEventListener('scroll', mou, true);
    return () => {
      document.removeEventListener('mousedown', fora);
      window.removeEventListener('resize', mou);
      window.removeEventListener('scroll', mou, true);
    };
  }, [obert, recolloca]);

  // Manté visible l'opció activa en navegar amb el teclat.
  useEffect(() => {
    if (!obert || actiuIdx < 0) return;
    const el = llista.current?.querySelector<HTMLElement>(`[data-idx="${actiuIdx}"]`);
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [actiuIdx, obert]);

  const mouActiu = (delta: number) => {
    if (filtrades.length === 0) return;
    let i = actiuIdx;
    for (let n = 0; n < filtrades.length; n++) {
      i = (i + delta + filtrades.length) % filtrades.length;
      if (!filtrades[i]?.desactivada) break;
    }
    setActiuIdx(i);
  };

  const tecla = (e: KeyboardEvent) => {
    if (!obert) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault();
        obre();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        mouActiu(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        mouActiu(-1);
        break;
      case 'Home':
        if (!ambCerca) {
          e.preventDefault();
          setActiuIdx(filtrades.indexOf(habilitades[0] as OpcioDesplegable<V>));
        }
        break;
      case 'End':
        if (!ambCerca) {
          e.preventDefault();
          setActiuIdx(
            filtrades.indexOf(habilitades[habilitades.length - 1] as OpcioDesplegable<V>),
          );
        }
        break;
      case 'Enter': {
        e.preventDefault();
        const o = filtrades[actiuIdx];
        if (o) tria(o);
        break;
      }
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        tanca();
        break;
      case 'Tab':
        tanca(false);
        break;
      default:
        // Sense cercador: salta a la primera opció que comença per la lletra premuda.
        if (!ambCerca && e.key.length === 1) {
          const k = normalitza(e.key);
          const idx = filtrades.findIndex(
            (o, i) => i > actiuIdx && !o.desactivada && normalitza(o.etiqueta).startsWith(k),
          );
          const idx2 =
            idx >= 0
              ? idx
              : filtrades.findIndex((o) => !o.desactivada && normalitza(o.etiqueta).startsWith(k));
          if (idx2 >= 0) setActiuIdx(idx2);
        }
    }
  };

  const etiquetaBoto = mostrada?.etiqueta ?? placeholder ?? t('accions.tria');
  const buit = !mostrada || (mostrada.valor === '' && !buida);
  const classe = [
    'desplegable',
    `desplegable--${variant}`,
    actiu ? 'is-active' : '',
    obert ? 'is-open' : '',
    buit && !mostrada ? 'is-placeholder' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  let grupAnterior: string | undefined;
  return (
    <>
      <button
        ref={boto}
        type="button"
        id={id}
        className={classe}
        role="combobox"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={obert}
        aria-controls={obert ? idLlista : undefined}
        disabled={disabled}
        onClick={() => (obert ? tanca() : obre())}
        onKeyDown={tecla}
        title={mostrada?.etiqueta}
      >
        {mostrada?.icona ? <span className="desplegable__icona">{mostrada.icona}</span> : null}
        <span className="desplegable__valor">{etiquetaBoto}</span>
        <Icona nom="chevron-down" className="desplegable__xebro" />
      </button>
      {obert
        ? createPortal(
            <div
              ref={llista}
              className="desplegable__llista"
              style={pos}
              onKeyDown={tecla}
              tabIndex={-1}
            >
              {ambCerca ? (
                <div className="desplegable__cerca">
                  <Icona nom="magnifier" />
                  <input
                    ref={camp}
                    value={text}
                    placeholder={t('accions.cerca')}
                    aria-label={t('accions.cerca')}
                    aria-controls={idLlista}
                    aria-activedescendant={actiuIdx >= 0 ? `${idBase}-o${actiuIdx}` : undefined}
                    onChange={(e) => {
                      setText(e.target.value);
                      setActiuIdx(0);
                    }}
                  />
                </div>
              ) : null}
              <div
                id={idLlista}
                role="listbox"
                aria-label={ariaLabel}
                className="desplegable__opcions"
                aria-activedescendant={
                  !ambCerca && actiuIdx >= 0 ? `${idBase}-o${actiuIdx}` : undefined
                }
              >
                {filtrades.length === 0 ? (
                  <div className="desplegable__buit">
                    {senseResultats ?? t('accions.senseResultats')}
                  </div>
                ) : (
                  filtrades.map((o, i) => {
                    const capGrup = o.grup && o.grup !== grupAnterior ? o.grup : null;
                    grupAnterior = o.grup;
                    const sel = o === mostrada;
                    return (
                      <div key={`${String(o.valor)}-${i}`} role="presentation">
                        {capGrup ? (
                          <div className="desplegable__grup" role="presentation">
                            {capGrup}
                          </div>
                        ) : null}
                        <div
                          id={`${idBase}-o${i}`}
                          data-idx={i}
                          role="option"
                          aria-selected={sel}
                          aria-disabled={o.desactivada || undefined}
                          className={`desplegable__opcio${i === actiuIdx ? ' is-focus' : ''}`}
                          style={o.nivell ? { paddingLeft: 12 + o.nivell * 16 } : undefined}
                          onMouseEnter={() => !o.desactivada && setActiuIdx(i)}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => tria(o)}
                        >
                          {o.icona ? <span className="desplegable__icona">{o.icona}</span> : null}
                          <span className="desplegable__text">
                            <span className="desplegable__etiqueta">{o.etiqueta}</span>
                            {o.detall ? (
                              <span className="desplegable__detall">{o.detall}</span>
                            ) : null}
                          </span>
                          {sel ? <Icona nom="checkmark" className="desplegable__check" /> : null}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
